import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, vShow as _vShow, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { id: "doodle-canvas-temp" }

import { computed, ref, watch } from 'vue'
import { RGBA } from '@/utils/rgba'
import DoodleLayer from '@/components/doodle/DoodleLayer.vue'
import DoodleToolbox from '@/components/doodle/DoodleToolbox.vue'
import DraggableContainer from '@/components/doodle/DraggableContainer.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'DoodleView',
  emits: ["closeDoodle"],
  setup(__props, { emit: __emit }) {

_useCssVars(_ctx => ({
  "14545dda": (doodleColor.value),
  "551c520c": (doodleBrushSizePx.value),
  "74b0f0c7": (doodleBrushX.value),
  "74b0f0c8": (doodleBrushY.value)
}))

const emits = __emit

const doodleBrushSize = ref<number>(2)
const doodleColor = ref<string>('#a8071a')
const doodleLayer = ref()
const doodleLayerId = ref<string>('doodle-layer')
const doodleMousePosition = ref<{ x: number; y: number }>({ x: 0, y: 0 })
const doodleText = ref<string>('')
const doodleTextInput = ref()
const doodleTextNewPosition = ref<{ left: string; top: string } | undefined>(undefined)
const doodleTextStartPosition = ref<{ left: string; top: string } | undefined>(undefined)
const doodleTextSvgSize = ref<{ height: number; width: number }>({ height: 0, width: 0 })
const doodleTool = ref<string>('free')
const hoverToolbox = ref<boolean>(false)
const showTempCanvas = ref<boolean>(false)

const doodleBrushSizePx = computed(() => doodleBrushSize.value + 'px')
const doodleBrushX = computed(() => doodleMousePosition.value.x - doodleBrushSize.value / 2 + 'px')
const doodleBrushY = computed(() => doodleMousePosition.value.y - doodleBrushSize.value / 2 + 'px')

// Foreign object used to hande line breaks and text wrapping in SVG
const doodleTextSvg = computed(() => {
  return `<svg xmlns="http://www.w3.org/2000/svg" height="${doodleTextSvgSize.value.height}" width="${doodleTextSvgSize.value.width}">
    <foreignObject x="0" y="0" height="${doodleTextSvgSize.value.height}" width="${doodleTextSvgSize.value.width}">
      <p xmlns="http://www.w3.org/1999/xhtml" style="${doodleTextSvgStyle.value}">
        ${doodleText.value}
      </p>
    </foreignObject>
  </svg>`
})
const doodleTextSvgStyle = computed(() => {
  // Using Arial font since it works with SVG without any additional setup
  // Regular hex color not accepted in SVG, so we convert it to RGB
  return `color: ${RGBA.fromHex(
    doodleColor.value
  )}; font-family: Arial, sans-serif; font-size: 17px; font-weight: bold; line-height: 24px; margin: 0; padding: 0 16px; white-space: normal; word-wrap: break-word;`
})

function changeDoodleBrushSize(value) {
  if (value === 'increase') {
    doodleBrushSize.value = doodleBrushSize.value + 2
  } else if (value === 'decrease' && doodleBrushSize.value > 2) {
    doodleBrushSize.value = doodleBrushSize.value - 2
  }
}
function closeDoodle() {
  doodleBrushSize.value = 2
  doodleColor.value = '#a8071a'
  doodleTool.value = 'free'
  emits('closeDoodle')
}
function closeDoodleText() {
  doodleTextNewPosition.value = undefined
  doodleTextStartPosition.value = undefined
  doodleText.value = ''
  doodleTextSvgSize.value = { height: 0, width: 0 }
}
function handleInputKeyup(event) {
  event.stopPropagation()
  if (event.key === 'Enter' && !event.shiftKey) {
    // If the textarea was dragged, use the new position, otherwise use the clicked start position
    const position = doodleTextNewPosition.value ?? doodleTextStartPosition.value
    // Let the SVG size match the textarea size
    doodleTextSvgSize.value = {
      height: doodleTextInput.value.clientHeight,
      width: doodleTextInput.value.clientWidth
    }
    // Remove newlines and replace double quotes with single quotes
    const doodleTextSvgEncoded = doodleTextSvg.value
      .replace(/(\r\n|\r|\n)/g, '<br />')
      .replace(/"/g, "'")
    // Create an image element
    const img = document.createElement('img')
    // Wait for the image to load before drawing it to the canvas
    // Adding 1 to left positioning for the border
    img.onload = () => {
      doodleLayer.value.drawImage(
        img,
        position ? parseInt(position.left) + 1 : 0,
        position ? parseInt(position.top) : 0
      )
    }
    // Load our SVG to the image and the text is drawn to the canvas
    img.src = `data:image/svg+xml,${doodleTextSvgEncoded}`
  }
  if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Escape') {
    closeDoodleText()
  }
}
function setDoodleMousePosition(value) {
  doodleMousePosition.value = value
}
async function setDoodleTextStartPosition(value: { x: number; y: number }) {
  doodleTextNewPosition.value = undefined
  doodleTextStartPosition.value = { left: value.x - 15 + 'px', top: value.y - 34 + 'px' } // Adjust for the textarea input to match mouse cursor
  await new Promise((resolve) => setTimeout(resolve, 100))
  doodleTextInput.value.focus()
}
watch(doodleTool, (newTool, oldTool) => {
  if (oldTool === 'text' && newTool !== 'text') {
    closeDoodleText()
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(DoodleToolbox, {
      class: "doodle-toolbox",
      onChangeDoodleBrushSize: changeDoodleBrushSize,
      onChangeDoodleColor: _cache[0] || (_cache[0] = ($event: any) => (doodleColor.value = $event)),
      onChangeDoodleTool: _cache[1] || (_cache[1] = ($event: any) => (doodleTool.value = $event)),
      onCloseDoodle: closeDoodle,
      onMouseenter: _cache[2] || (_cache[2] = ($event: any) => (hoverToolbox.value = true)),
      onMouseleave: _cache[3] || (_cache[3] = ($event: any) => (hoverToolbox.value = false))
    }),
    (doodleTool.value !== 'text' && !hoverToolbox.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          id: "doodle-brush",
          class: _normalizeClass({ eraser: doodleTool.value === 'eraser', rectangle: doodleTool.value === 'rectangle' })
        }, null, 2))
      : _createCommentVNode("", true),
    _cache[7] || (_cache[7] = _createElementVNode("div", { id: "doodle-frame" }, null, -1)),
    _withDirectives(_createVNode(DraggableContainer, {
      background: "'transparent'",
      class: "doodle-text",
      startPosition: doodleTextStartPosition.value,
      onNewPosition: _cache[5] || (_cache[5] = ($event: any) => (doodleTextNewPosition.value = $event))
    }, {
      content: _withCtx(() => [
        _withDirectives(_createElementVNode("textarea", {
          ref_key: "doodleTextInput",
          ref: doodleTextInput,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((doodleText).value = $event)),
          class: "doodle-text__textarea",
          onKeyup: handleInputKeyup
        }, null, 544), [
          [_vModelText, doodleText.value]
        ])
      ]),
      _: 1
    }, 8, ["startPosition"]), [
      [_vShow, doodleTextStartPosition.value]
    ]),
    _withDirectives(_createElementVNode("canvas", _hoisted_1, null, 512), [
      [_vShow, showTempCanvas.value]
    ]),
    _createVNode(DoodleLayer, {
      id: doodleLayerId.value,
      ref_key: "doodleLayer",
      ref: doodleLayer,
      doodleBrushSize: doodleBrushSize.value,
      doodleColor: doodleColor.value,
      doodleLayerId: doodleLayerId.value,
      doodleTool: doodleTool.value,
      onEscapeKeyup: closeDoodle,
      onMousePosition: setDoodleMousePosition,
      onShowTempCanvas: _cache[6] || (_cache[6] = ($event: any) => (showTempCanvas.value = $event)),
      onTextPosition: setDoodleTextStartPosition
    }, null, 8, ["id", "doodleBrushSize", "doodleColor", "doodleLayerId", "doodleTool"])
  ], 64))
}
}

})